<script setup lang="ts">
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { closeLoading, openLoading } from '@/components/business/loading/loading'
import { handleErr } from '@/composables/exception/handler'
import {
  deleteMemorialMessage,
  type MemorialGiftDTO,
  type MemorialMessageDTO,
  type RemoveMemorialMessageResponse
} from '@/composables/memorize/memorize'
import { currentUserHandle } from '@/composables/user/users'
import { minifyString, renderMessage } from '@/utils/util'
import { computed, ref, watch, type PropType, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElPopover, ElMessageBox, ElMessage } from 'element-plus'
import { currentLang } from '@/composables/settings/storage'
import { useGiftStore } from '@/composables/memorize/gift'

const { t } = useI18n()

// script setup
const model = defineModel<boolean>()
const props = defineProps({
  item: {
    type: Object as PropType<MemorialMessageDTO>
  },
  anim: {
    type: Boolean,
    default: false,
    required: false
  },
  canManage: {
    type: Boolean,
    default: true,
    required: false
  },
  activeMenuItem: {
    type: String,
    default: null,
    required: false
  },
  showGroup: {
    type: Boolean,
    default: false,
    required: false
  }
})

const showGroup = computed(() => props.showGroup)

const animPlayed = ref(false)
const message = computed(() => props.item)

const reverse = ref(false)
const moreVisible = ref(false)
let moreRef = ref(null)

// deleteMemorialMessage
const emit = defineEmits(['onDelete', 'animationEnd', 'setActiveMenuItem'])

// 监听activeMenuItem的变化，如果不是当前项，则关闭菜单
watch(
  () => props.activeMenuItem,
  (newVal) => {
    if (newVal !== message.value.id) {
      moreVisible.value = false
    }
  }
)

// 监控moreVisible的变化，当打开菜单时通知父组件
watch(
  () => moreVisible.value,
  (newVal) => {
    if (newVal) {
      emit('setActiveMenuItem', message.value.id)
    }
  }
)

watch(
  () => props.anim,
  (val) => {
    animPlayed.value = val
    setTimeout(() => {
      emit('animationEnd')
      console.log('animationEnd')
    }, 1500)
  }
)

function judgeDelete() {
  moreVisible.value = false
  ElMessageBox.confirm(t('m.confirm_delete_gift'), {
    confirmButtonText: t('action.confirm'),
    cancelButtonText: t('action.cancel'),
    type: 'warning'
  })
    .then(() => {
      toDelete()
    })
    .catch(() => {})
}

async function toDelete() {
  moreVisible.value = false
  openLoading(t('alt.loading'))
  const { data, error, statusCode } = await deleteMemorialMessage(message.value.id)
  closeLoading()
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<RemoveMemorialMessageResponse>>
    if (resData.value.code === CODE_OK) {
      // success
      const response: RemoveMemorialMessageResponse = resData.value.data
      emit('onDelete', response.msgId)
      ElMessage({
        message: renderMessage(t('common.delete_success')),
        type: 'success'
      })
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

const canDeleteMessage = computed(() => {
  return props.canManage || message.value.creator?.id === currentUserHandle.value
})

const giftStore = useGiftStore()

const packGifts = computed(() => {
  if (
    giftStore.getGiftList == undefined ||
    giftStore.getGiftList.length == 0 ||
    message.value.gift == null ||
    !(message.value.gift?.pack || false)
  ) {
    return []
  } else {
    const giftList = giftStore.getGiftList as MemorialGiftDTO[]
    const result: MemorialGiftDTO[] = []
    for (const gift of giftList) {
      if (message.value.gift?.idsInPack.includes(gift.id)) {
        result.push(gift)
      }
    }
    return result
  }
})
</script>

<template>
  <div
    :class="animPlayed ? 'animated-background' : ''"
    flex="~ col"
    bg-theme-light
    rounded-2
    p-2
    pt-4
    py-3
    items-center
    relative>
    <div flex="~ row" justify-end items-end w-full absolute top-0 right-0>
      <div ref="moreRef" ml-2>
        <RIcon
          v-if="canDeleteMessage"
          icon="i-system-uicons:menu-vertical"
          rounded-2
          :reverse="reverse"
          :grey="true"
          size="medium"
          @click.prevent.stop="moreVisible = !moreVisible"></RIcon>
        <RToolTip :wrapper-ref="moreRef" placement="bottom" :arrow="false" v-model="moreVisible">
          <div flex flex-row items-center text-theme-dark>
            <div flex="~ row" items-center quick-menu-item @click="judgeDelete()" text-danger>
              <i i-mi:delete text-base></i>
              <p px-2>{{ $t('action.delete') }}</p>
            </div>
          </div>
        </RToolTip>
      </div>
    </div>
    <div w-26 h-26 mt-1 relative>
      <el-popover
        v-if="message.gift?.pack || false"
        :width="360"
        effect="dark"
        trigger="click"
        popper-style="box-shadow: rgb(14 18 22 / 35%) 0px 10px 38px -10px, rgb(14 18 22 / 20%) 0px 10px 20px -15px; padding: 20px;">
        <template #reference>
          <GiftItem v-if="message.gift" :gift="message.gift" :isPreview="false" :size="24" cursor-pointer />
        </template>
        <template #default>
          <div text-sm mb-4>{{ $t('gift.pack_include', { ctn: packGifts?.length }) }}</div>
          <div w-full text-xs text-theme-inverted grid grid-cols-5 gap-2>
            <div v-for="(item, index) in packGifts" :key="index" flex="~ col" items-center justify-center pb-1>
              <GiftItem :gift="item" :isPreview="false" :size="18" cursor-pointer />
              <div text-xs py-1px text-third truncate>{{ item?.giftNames?.data[currentLang] }}</div>
            </div>
          </div>
        </template>
      </el-popover>
      <GiftItem
        v-if="message.gift && !(message.gift?.pack || false)"
        :gift="message.gift"
        :isPreview="false"
        :size="26" />
      <div
        v-if="message.gift?.pack"
        text-main
        z-1
        flex
        items-center
        justify-center
        absolute
        top-20
        bg-base
        rounded-full
        px-2
        py-1
        opacity-80
        w-full
        text-center
        text-xs
        pointer-events-none>
        {{ $t('gift.pack_tip') }}
      </div>
      <IconStar v-if="(message.gift?.price || 0) > 0" class="star-glow" />
      <IconStar v-if="(message.gift?.price || 0) > 0" class="star-glow2" />
    </div>
    <div w-full flex="~ row" justify-between items-center mt-2 mb-1>
      <div text-sm text-theme-gray truncate leading-6>
        {{ minifyString(message.gift?.giftNames?.data[currentLang], 10) || '' }}
      </div>
      <el-popover
        v-if="message.tags && message.tags.length > 0"
        :width="160"
        trigger="click"
        popper-style="box-shadow: rgb(14 18 22 / 35%) 0px 10px 38px -10px, rgb(14 18 22 / 20%) 0px 10px 20px -15px; padding: 20px;">
        <template #reference>
          <div relative w-6 h-6 rounded-full>
            <div inset-0 w-6 h-6 rounded-full bg-theme-gray opacity-10></div>
            <span absolute w-6 h-6 top-0 left-0 right-0 bottom-0 flex-r-center text-theme-gray text-xs>#</span>
          </div>
        </template>
        <template #default>
          <div w-full flex="~ row wrap" justify-start grow>
            <RTag v-for="(item, index) in message.tags" :key="index" :title="item.name" mr-2 :simple="true"></RTag>
          </div>
        </template>
      </el-popover>
    </div>
    <div w-full flex="~ row">
      <SignatureItem
        :avatar="message?.creator ? message?.creator?.avatar : null"
        :hideAvatar="true"
        :smallName="true"
        :nameLimit="5"
        :loose="true"
        :wechatInfo="message?.creator ? message?.creator?.wechatUserInfo : null"
        :fullName="message.creator?.name?.fullName || ''"
        :relationName="message.relation?.name || ''"
        :displayTime="message.createdAt"
        :suffix="$t('gift.dedicate')" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* 定义星星闪烁的动画关键帧 */
@keyframes twinkle {
  0% {
    transform: scale(0.5);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(0.5);
    opacity: 0.1;
  }
}

/* 绘制星星的光芒 */
.star-glow {
  position: absolute;
  top: 4px;
  right: 22px;
  width: 18px;
  height: 18px;
  animation: twinkle 6s infinite;
  opacity: 0.9;
  z-index: 10;
}

/* 绘制星星的光芒 */
.star-glow2 {
  position: absolute;
  top: 28px;
  right: 56px;
  width: 14px;
  height: 14px;
  animation: twinkle 6s infinite;
  animation-delay: 2s;
  opacity: 0.8;
  z-index: 10;
}
</style>
